import { useCallback, useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
} from "reactstrap"
import * as Yup from "yup"

import { CanView } from "components/Common/CanView"
import InputField from "components/Common/InputField"
import PdfAttachmentUpload, {
  uploadPendingPdfAttachments,
} from "components/Common/PdfAttachmentUpload"
import ThemeModal from "components/Common/ThemeModal"
import ToggleButton from "components/Common/ToggleButton"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { ACCESS } from "constants/accessPermissions"
import { useLcDocuments } from "hooks/useLcDocuments"
import {
  useBulkCreateVehicleLcDocuments,
  useCreateVehicleLcDocument,
  useEditVehicleLcDocument,
  useVehicleLcDocuments,
} from "hooks/useVehicleLcDocuments"

const documentKey = (documentId, isAttested = false) =>
  `${documentId}:${isAttested ? "attested" : "regular"}`

const emptyDocumentRow = (document, isAttested = false) => ({
  lc_document_id: document.id,
  document_date: "",
  is_attested: isAttested,
  sort_order: document.sort_order != null ? document.sort_order : 0,
  remarks: "",
})

const existingDocumentRow = (document, fallbackSortOrder = 0) => ({
  id: document.id,
  lc_document_id: document.lc_document_id,
  document_date: document.document_date
    ? String(document.document_date).slice(0, 10)
    : "",
  is_attested: document.is_attested != null ? document.is_attested : false,
  sort_order: document.sort_order != null ? document.sort_order : fallbackSortOrder,
  remarks: document.remarks || "",
})

const documentDisplayName = (document, isAttested = false) =>
  `${document.name}${isAttested ? " (Attested)" : ""}`

const acknowledgementAttachments = data =>
  Array.isArray(data && data.lc_document_acknowledgement && data.lc_document_acknowledgement.attachments)
    ? data.lc_document_acknowledgement.attachments
    : []

const AttestedToggle = ({ checked, disabled, onChange }) => (
  <ToggleButton active={checked} disabled={disabled} onClick={onChange}>
    {checked ? "Attested" : "Not Attested"}
  </ToggleButton>
)

const RemoveDocumentModal = ({ show, loading = false, onRemoveClick, onCloseClick }) => (
  <Modal size="md" isOpen={show} toggle={onCloseClick} centered>
    <div className="modal-content">
      <ModalBody className="px-4 py-5 text-center">
        <button
          type="button"
          onClick={onCloseClick}
          className="btn-close position-absolute end-0 top-0 m-3"
        ></button>
        <div className="avatar-sm mb-4 mx-auto">
          <div className="avatar-title bg-warning text-warning bg-opacity-10 font-size-20 rounded-3">
            <i className="mdi mdi-minus-circle-outline"></i>
          </div>
        </div>
        <p className="text-muted font-size-16 mb-4">
          Remove this LC document from the vehicle? Submit the form to save the change.
        </p>

        <div className="hstack gap-2 justify-content-center mb-0">
          <button
            type="button"
            className="btn btn-warning"
            onClick={onRemoveClick}
            disabled={loading}
          >
            {loading ? "Removing..." : "Remove"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCloseClick}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </ModalBody>
    </div>
  </Modal>
)

const VehicleIdField = ({ value, touched, error, onChange, onVehicleSelect }) => {
  const vehicleFormik = useMemo(
    () => ({
      values: { vehicle_id: value || "" },
      touched: { vehicle_id: touched },
      errors: { vehicle_id: error },
      setFieldValue: (field, fieldValue) => {
        if (field === "vehicle_id") {
          onChange(fieldValue || "")
        }
      },
    }),
    [error, onChange, touched, value]
  )

  return (
    <div className="mb-3">
      <VehicleIdSelect
        formik={vehicleFormik}
        onVehicleSelect={onVehicleSelect}
      />
    </div>
  )
}

const VehicleLcDocumentAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editFull = false,
  editedData,
  defaultVehicleId,
  onSaved,
}) => {
  const [vehicleStockId, setVehicleStockId] = useState(defaultVehicleId || "")
  const [selectedDocuments, setSelectedDocuments] = useState({})
  const [attachments, setAttachments] = useState([])
  const [attachmentUploadLoading, setAttachmentUploadLoading] = useState(false)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([])

  const { data: lcDocumentResponse } = useLcDocuments(
    { all: true, is_active: 1 },
    modal
  )
  const { data: existingDocumentsResponse } = useVehicleLcDocuments(
    { vehicle_stock_id: vehicleStockId, all: true },
    modal && !isEdit && !!vehicleStockId
  )

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateVehicleLcDocument()
  const { mutateAsync: bulkCreateMutation, isPending: bulkCreateLoading } =
    useBulkCreateVehicleLcDocuments()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditVehicleLcDocument()

  const lcDocuments = useMemo(
    () =>
      (lcDocumentResponse && lcDocumentResponse.data || []).slice().sort(
        (a, b) => (a.sort_order != null ? a.sort_order : 0) - (b.sort_order != null ? b.sort_order : 0)
      ),
    [lcDocumentResponse]
  )
  const defaultDocuments = useMemo(
    () => lcDocuments.filter(document => document.is_default),
    [lcDocuments]
  )
  const removedDocumentsByKey = useMemo(() => {
    return (existingDocumentsResponse && existingDocumentsResponse.data || [])
      .filter(document => deletedDocumentIds.includes(document.id))
      .reduce((documents, document) => {
        documents[documentKey(document.lc_document_id, document.is_attested)] = document
        return documents
      }, {})
  }, [deletedDocumentIds, existingDocumentsResponse])
  const removedDocumentKeys = useMemo(
    () => new Set(Object.keys(removedDocumentsByKey)),
    [removedDocumentsByKey]
  )
  const optionalDocuments = useMemo(
    () =>
      lcDocuments.filter(document => {
        if (!document.is_default) return true

        return removedDocumentKeys.has(documentKey(document.id, false)) ||
          removedDocumentKeys.has(documentKey(document.id, true))
      }),
    [lcDocuments, removedDocumentKeys]
  )
  const existingDocumentKeys = useMemo(
    () =>
      new Set(
        (existingDocumentsResponse && existingDocumentsResponse.data || [])
          .filter(document => !deletedDocumentIds.includes(document.id))
          .map(document =>
            documentKey(document.lc_document_id, document.is_attested)
          )
      ),
    [deletedDocumentIds, existingDocumentsResponse]
  )
  const existingDocumentsByKey = useMemo(() => {
    return (existingDocumentsResponse && existingDocumentsResponse.data || [])
      .filter(document => !deletedDocumentIds.includes(document.id))
      .reduce((documents, document) => {
      documents[documentKey(document.lc_document_id, document.is_attested)] = document
      return documents
    }, {})
  }, [deletedDocumentIds, existingDocumentsResponse])

  useEffect(() => {
    if (!modal || isEdit) return

    setVehicleStockId(defaultVehicleId || "")
    setAttachments(acknowledgementAttachments(editedData))
    setDeletedDocumentIds([])
  }, [defaultVehicleId, editedData, isEdit, modal])

  useEffect(() => {
    if (!modal || isEdit || !lcDocuments.length) return

    setSelectedDocuments(current => {
      const next = Object.assign({}, current)

      defaultDocuments.forEach(document => {
        const key = documentKey(document.id, false)
        if (!existingDocumentKeys.has(key) && !removedDocumentKeys.has(key) && !next[key]) {
          next[key] = emptyDocumentRow(document)
        }
      })

      Object.keys(next).forEach(documentId => {
        if (existingDocumentKeys.has(documentId)) {
          delete next[documentId]
        }
      })

      return next
    })
  }, [defaultDocuments, existingDocumentKeys, isEdit, lcDocuments.length, modal, removedDocumentKeys])

  const validationSchema = Yup.object({
    vehicle_stock_id: Yup.string().required("Please select vehicle"),
    lc_document_id: Yup.string().required("Please select document"),
    sort_order: Yup.number().nullable().min(0, "Sort order cannot be negative"),
  })

  const onSubmit = useCallback(
    (values, actions) => {
      const resetForm = actions.resetForm
      if (isEdit && deletedDocumentIds.length) {
        return bulkCreateMutation({
          vehicle_stock_id: Number(values.vehicle_stock_id),
          documents: [],
          deleted_document_ids: deletedDocumentIds,
        })
          .then(res => {
            toast.success(res && res.message || "Vehicle LC document removed successfully.")
            resetForm()
            setDeletedDocumentIds([])
            toggleModal()
            if (onSaved) onSaved()
          })
          .catch(() => {
            toast.error("Failed to remove vehicle LC document.")
          })
      }

      const payload = {
        vehicle_stock_id: Number(values.vehicle_stock_id),
        lc_document_id: Number(values.lc_document_id),
        document_date: values.document_date || null,
        is_attested: values.is_attested,
        sort_order: values.sort_order || 0,
        remarks: values.remarks ? values.remarks.trim() : null,
      }

      const action = isEdit
        ? editMutation(Object.assign({ id: editedData.id }, payload))
        : createMutation(payload)

      return action
        .then(res => {
        toast.success(
          res && res.message ||
            `Vehicle LC document ${
              isEdit ? "updated" : "created"
            } successfully.`
        )
        resetForm()
        toggleModal()
        if (onSaved) onSaved()
        })
        .catch(() => {
          toast.error("Failed to save vehicle LC document.")
        })
    },
    [bulkCreateMutation, createMutation, deletedDocumentIds, editMutation, editedData, isEdit, onSaved, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_stock_id:
        editedData && editedData.vehicle_stock_id ||
        editedData && editedData.vehicleStock && editedData.vehicleStock.id ||
        defaultVehicleId ||
        "",
      lc_document_id:
        editedData && editedData.lc_document_id || editedData && editedData.lcDocument && editedData.lcDocument.id || "",
      document_date: editedData && editedData.document_date
        ? String(editedData.document_date).slice(0, 10)
        : "",
      is_attested: editedData && editedData.is_attested != null ? editedData.is_attested : false,
      sort_order: editedData && editedData.sort_order != null ? editedData.sort_order : 0,
      remarks: editedData && editedData.remarks || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const updateDisplayedDocument = (key, row, field, value) => {
    setSelectedDocuments(current =>
      Object.assign({}, current, {
        [key]: Object.assign({}, row, current[key] || {}, { [field]: value }),
      })
    )
  }

  const handleVehicleChange = value => {
    setVehicleStockId(value)
    setSelectedDocuments({})
    setAttachments([])
    setDeletedDocumentIds([])
  }

  const toggleDocument = (document, isAttested = false) => {
    const key = documentKey(document.id, isAttested)
    setSelectedDocuments(current => {
      if (current[key]) {
        const next = Object.assign({}, current)
        delete next[key]
        return next
      }

      const removedDocument = removedDocumentsByKey[key]
      if (removedDocument) {
        setDeletedDocumentIds(ids => ids.filter(id => id !== removedDocument.id))
        return Object.assign({}, current, {
          [key]: existingDocumentRow(removedDocument, document.sort_order != null ? document.sort_order : 0),
        })
      }

      return Object.assign({}, current, { [key]: emptyDocumentRow(document, isAttested) })
    })
  }

  const toggleAttestedCopy = (key, document, row, isExisting) => {
    if (isExisting && row.is_attested) return

    const attestedKey = documentKey(document.id, true)

    setSelectedDocuments(current => {
      if (row.is_attested) {
        const next = Object.assign({}, current)
        delete next[key]
        return next
      }

      if (existingDocumentKeys.has(attestedKey) || current[attestedKey]) {
        toast.info("Attested copy already exists for this document.")
        return current
      }

      return Object.assign({}, current, {
        [attestedKey]: Object.assign({}, row, { is_attested: true }),
      })
    })
  }

  const removeSelectedDocument = key => {
    setSelectedDocuments(current => {
      const next = Object.assign({}, current)
      delete next[key]
      return next
    })
  }

  const handleRemoveClick = target => {
    if (target.row && target.row.id) {
      setRemoveTarget(target)
      return
    }

    removeSelectedDocument(target.key)
  }

  const confirmRemove = () => {
    if (!removeTarget || !removeTarget.row || !removeTarget.row.id) return

    setDeletedDocumentIds(current =>
      current.includes(removeTarget.row.id)
        ? current
        : current.concat([removeTarget.row.id])
    )
    removeSelectedDocument(removeTarget.key)
    setRemoveTarget(null)
    toast.info("Document marked for removal. Submit to save changes.")
  }

  const selectedRows = useMemo(
    () =>
      Object.entries(selectedDocuments)
        .map(([key, row]) => {
          const document = lcDocuments.find(
            lcDocument => Number(lcDocument.id) === Number(row.lc_document_id)
          )

          return document
            ? {
                key,
                document,
                row,
              }
            : null
        })
        .filter(Boolean),
    [lcDocuments, selectedDocuments]
  )

  const displayRows = useMemo(() => {
    const rows = defaultDocuments
      .map(document => {
        const key = documentKey(document.id, false)
        const existingDocument = existingDocumentsByKey[key]

        if (removedDocumentKeys.has(key) && !selectedDocuments[key]) {
          return null
        }

        return {
          key,
          document,
          isExisting: !!existingDocument || !!(selectedDocuments[key] && selectedDocuments[key].id),
          row:
            selectedDocuments[key] ||
            (existingDocument
              ? existingDocumentRow(existingDocument, document.sort_order != null ? document.sort_order : 0)
              : emptyDocumentRow(document)),
        }
      })
      .filter(Boolean)

    defaultDocuments.forEach(document => {
      const key = documentKey(document.id, true)
      const existingDocument = existingDocumentsByKey[key]
      const selectedDocument = selectedDocuments[key]

      if (existingDocument || selectedDocument) {
          rows.push({
            key,
            document,
            isExisting: !!existingDocument || !!(selectedDocument && selectedDocument.id),
            row:
              selectedDocument ||
              existingDocumentRow(existingDocument, document.sort_order != null ? document.sort_order : 0),
        })
      }
    })

    if (editFull) {
      Object.entries(existingDocumentsByKey).forEach(([key, existingDocument]) => {
        const alreadyVisible = rows.some(row => row.key === key)
        const document = lcDocuments.find(
          lcDocument =>
            Number(lcDocument.id) === Number(existingDocument.lc_document_id)
        )

        if (!alreadyVisible && document) {
          rows.push({
            key,
            document,
            isExisting: true,
            row:
              selectedDocuments[key] ||
              existingDocumentRow(existingDocument, document.sort_order != null ? document.sort_order : 0),
          })
        }
      })
    }

    selectedRows
      .filter(({ document }) => !document.is_default)
      .forEach(row => {
        if (!rows.some(displayRow => displayRow.key === row.key)) {
          rows.push(Object.assign({}, row, { isExisting: !!(row.row && row.row.id) }))
        }
      })

    return rows
  }, [
    defaultDocuments,
    editFull,
    existingDocumentsByKey,
    lcDocuments,
    removedDocumentKeys,
    selectedDocuments,
    selectedRows,
  ])

  const handleBulkSubmit = e => {
    e.preventDefault()

    if (!vehicleStockId) {
      toast.error("Please select vehicle.")
      return
    }

    if (!selectedRows.length && !deletedDocumentIds.length && !editFull) {
      toast[editFull ? "info" : "error"](
        editFull
          ? "No document changes to save."
          : "Please select at least one LC document."
      )
      return
    }

    const changedExistingRows = selectedRows.filter(({ row }) => row.id)
    const newRows = selectedRows.filter(({ row }) => !row.id)
    const documents = newRows.map(({ row }) => ({
      lc_document_id: Number(row.lc_document_id),
      document_date: row.document_date || null,
      is_attested: row.is_attested,
      sort_order: row.sort_order || 0,
      remarks: row.remarks ? row.remarks.trim() : null,
    }))

    setAttachmentUploadLoading(true)
    uploadPendingPdfAttachments(attachments)
      .then(uploadedAttachments => {
        setAttachments(uploadedAttachments)
        setAttachmentUploadLoading(false)

        return Promise.all(
          changedExistingRows.map(({ row }) =>
            editMutation({
              id: row.id,
              vehicle_stock_id: Number(vehicleStockId),
              lc_document_id: Number(row.lc_document_id),
              document_date: row.document_date || null,
              is_attested: row.is_attested,
              sort_order: row.sort_order || 0,
              remarks: row.remarks ? row.remarks.trim() : null,
            })
          )
        ).then(() => {
          if (documents.length || editFull) {
            return bulkCreateMutation({
              vehicle_stock_id: Number(vehicleStockId),
              documents,
              deleted_document_ids: deletedDocumentIds,
              attachments: uploadedAttachments,
            })
          }

          return null
        })
      })
      .then(() => {
        toast.success(
          editFull
            ? "Vehicle LC documents updated successfully."
            : "Vehicle LC documents created successfully."
        )
        setSelectedDocuments({})
        setDeletedDocumentIds([])
        toggleModal()
        if (onSaved) onSaved()
      })
      .catch(error => {
        setAttachmentUploadLoading(false)
        toast.error(error && error.message || "Failed to save vehicle LC documents.")
      })
  }

  const isSubmitDisabled =
    createLoading ||
    editLoading ||
    bulkCreateLoading ||
    attachmentUploadLoading

  const renderEditForm = () => (
    <Form
      onSubmit={e => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      <Card className="border">
        <CardBody>
          <VehicleIdField
            value={formik.values.vehicle_stock_id}
            touched={formik.touched.vehicle_stock_id}
            error={formik.errors.vehicle_stock_id}
            onChange={value => formik.setFieldValue("vehicle_stock_id", value)}
          />

          <Row>
            <Col md={8}>
              <div className="mb-3">
                <Label htmlFor="lc-document-id">LC Document</Label>
                <InputField
                  id="lc-document-id"
                  name="lc_document_id"
                  type="select"
                  value={formik.values.lc_document_id}
                  onChange={formik.handleChange}
                  invalid={
                    formik.touched.lc_document_id &&
                    !!formik.errors.lc_document_id
                  }
                >
                  <option value="">Select document</option>
                  {lcDocuments.map(document => (
                    <option key={document.id} value={document.id}>
                      {document.name}
                    </option>
                  ))}
                </InputField>
                {formik.touched.lc_document_id &&
                  formik.errors.lc_document_id && (
                    <FormFeedback type="invalid">
                      {formik.errors.lc_document_id}
                    </FormFeedback>
                  )}
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="vehicle-lc-sort-order">Sort Order</Label>
                <InputField
                  id="vehicle-lc-sort-order"
                  name="sort_order"
                  type="number"
                  min="0"
                  value={formik.values.sort_order}
                  onChange={formik.handleChange}
                  invalid={
                    formik.touched.sort_order && !!formik.errors.sort_order
                  }
                />
                {formik.touched.sort_order && formik.errors.sort_order && (
                  <FormFeedback type="invalid">
                    {formik.errors.sort_order}
                  </FormFeedback>
                )}
              </div>
            </Col>
          </Row>

          <div className="mb-3">
            <Label htmlFor="vehicle-lc-date">Date</Label>
            <InputField
              id="vehicle-lc-date"
              name="document_date"
              type="date"
              value={formik.values.document_date}
              onChange={formik.handleChange}
            />
          </div>

          <div className="mb-3">
            <Label htmlFor="vehicle-lc-remarks">Remarks</Label>
            <InputField
              id="vehicle-lc-remarks"
              name="remarks"
              type="textarea"
              value={formik.values.remarks}
              onChange={formik.handleChange}
            />
          </div>

          <AttestedToggle
            checked={formik.values.is_attested}
            onChange={() =>
              formik.setFieldValue("is_attested", !formik.values.is_attested)
            }
          />
        </CardBody>
      </Card>

      <div className="d-flex justify-content-end gap-2 mt-3">
        {isEdit && editedData && editedData.id && (
          <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.DELETE]}>
            <Button
              color="danger"
              type="button"
              outline
              disabled={isSubmitDisabled}
              onClick={() =>
                handleRemoveClick({
                  key: documentKey(
                    formik.values.lc_document_id,
                    formik.values.is_attested
                  ),
                  row: { id: editedData.id },
                  document: editedData.lcDocument,
                })
              }
            >
              <i className="mdi mdi-minus-circle-outline me-1" />
              Remove
            </Button>
          </CanView>
        )}
        <Button color="success" type="submit" disabled={isSubmitDisabled}>
          Save Changes{isSubmitDisabled ? "..." : ""}
        </Button>
      </div>
    </Form>
  )

  const renderBulkForm = () => (
    <Form onSubmit={handleBulkSubmit}>
      <Card className="border">
        <CardBody>
          <VehicleIdField
            value={vehicleStockId}
            onChange={handleVehicleChange}
          />

          <div className="mb-3">
            <Label>Attachment PDFs</Label>
            <PdfAttachmentUpload
              attachments={attachments}
              onChange={setAttachments}
              maxFiles={10}
            />
          </div>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0">LC Documents</h6>
            <div>
              <Badge className="badge-soft-success me-1">
                {selectedRows.length} Selected
              </Badge>
              <Badge className="badge-soft-secondary">
                {existingDocumentKeys.size} Existing
              </Badge>
            </div>
          </div>

          <div className="border rounded p-2 mb-3">
            <div className="fw-semibold mb-2">Optional Documents</div>
            <div className="d-flex flex-wrap gap-2">
              {optionalDocuments.map(document => {
                const regularKey = documentKey(document.id, false)
                const attestedKey = documentKey(document.id, true)
                const regularAdded = existingDocumentKeys.has(regularKey)
                const attestedAdded = existingDocumentKeys.has(attestedKey)
                const regularRemoved = removedDocumentKeys.has(regularKey)
                const attestedRemoved = removedDocumentKeys.has(attestedKey)
                return (
                  <div key={document.id} className="d-flex gap-1">
                    <ToggleButton
                      active={!!selectedDocuments[regularKey] || regularAdded}
                      disabled={regularAdded}
                      onClick={() => toggleDocument(document)}
                    >
                      {document.name}
                      {regularAdded ? " (Added)" : regularRemoved ? " (Removed)" : ""}
                    </ToggleButton>
                    <ToggleButton
                      active={!!selectedDocuments[attestedKey] || attestedAdded}
                      disabled={attestedAdded}
                      onClick={() => toggleDocument(document, true)}
                    >
                      {documentDisplayName(document, true)}
                      {attestedAdded ? " (Added)" : attestedRemoved ? " (Removed)" : ""}
                    </ToggleButton>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 220 }}>Document</th>
                  <th style={{ minWidth: 145 }}>Date</th>
                  <th style={{ minWidth: 220 }}>Remarks</th>
                  <th style={{ width: 120 }}>Attested</th>
                  <th style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(({ key, document, row, isExisting }) => (
                  <tr key={key}>
                    <td>
                      <div className="fw-semibold">
                        {documentDisplayName(document, row.is_attested)}
                      </div>
                      {document.is_default && (
                        <Badge className="badge-soft-info me-1">Default</Badge>
                      )}
                      {isExisting && (
                        <Badge className="badge-soft-secondary">Added</Badge>
                      )}
                    </td>
                    <td>
                      <Input
                        type="date"
                        value={row.document_date}
                        disabled={isExisting && !editFull}
                        onChange={e =>
                          updateDisplayedDocument(
                            key,
                            row,
                            "document_date",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Input
                        value={row.remarks}
                        disabled={isExisting && !editFull}
                        onChange={e =>
                          updateDisplayedDocument(
                            key,
                            row,
                            "remarks",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <AttestedToggle
                        checked={row.is_attested}
                        disabled={isExisting && row.is_attested && !editFull}
                        onChange={() =>
                          toggleAttestedCopy(key, document, row, isExisting)
                        }
                      />
                    </td>
                    <td>
                      {!isExisting && (
                        <Button
                          color="danger"
                          type="button"
                          size="sm"
                          outline
                          disabled={
                            isSubmitDisabled ||
                            (!isExisting && !selectedDocuments[key])
                          }
                          onClick={() =>
                            handleRemoveClick({
                              key,
                              document,
                              row,
                              isExisting,
                            })
                          }
                          title="Remove"
                        >
                          <i className="mdi mdi-minus-circle-outline" />
                        </Button>
                      )}
                      {isExisting && editFull && (
                        <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.DELETE]}>
                          <Button
                            color="danger"
                            type="button"
                            size="sm"
                            outline
                            disabled={isSubmitDisabled}
                            onClick={() =>
                              handleRemoveClick({
                                key,
                                document,
                                row,
                                isExisting,
                              })
                            }
                            title="Remove"
                          >
                            <i className="mdi mdi-minus-circle-outline" />
                          </Button>
                        </CanView>
                      )}
                    </td>
                  </tr>
                ))}
                {!displayRows.length && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      No LC documents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <div className="text-end mt-3">
        <Button color="success" type="submit" disabled={isSubmitDisabled}>
          {editFull ? "Save Documents" : "Add Vehicle LC Docs"}
          {attachmentUploadLoading ? " (Compressing PDFs...)" : ""}
          {isSubmitDisabled && !attachmentUploadLoading ? "..." : ""}
        </Button>
      </div>
    </Form>
  )

  return (
    <>
      <ThemeModal
        isOpen={modal}
        toggle={toggleModal}
        title={
          isEdit
            ? "Edit Vehicle LC Doc"
            : editFull
              ? "Edit Vehicle LC Docs"
              : "Add Vehicle LC Doc"
        }
        size={isEdit ? "lg" : "xl"}
      >
        {isEdit ? renderEditForm() : renderBulkForm()}
      </ThemeModal>

      <RemoveDocumentModal
        show={!!removeTarget}
        loading={false}
        onRemoveClick={confirmRemove}
        onCloseClick={() => setRemoveTarget(null)}
      />
    </>
  )
}

export default VehicleLcDocumentAddEdit
